import { AnalogInput, AnalogTemperatureSensor, BoilerAdapter, Buzzer, ConditionalOperator, GuardZone, HeatingCircuit, LogicExpression, Pump, RadioSensor, RadioSensor433, RadioSensorValue, RelayControl, SensorReading, StopScenario, ThreewayTap, TimeComparator, WiredTemperatureSensor, } from 'z3k-config';
import { FixedBlockType } from '../../types/block-type';
import { BlocklyError } from 'console/blockly/utils/blockly-error';
import { getZ3kEntityById } from 'z3k-parser/lib/auxiliary';
import { getUnitTime } from 'console/blockly/utils/time';
import { prettyFloat } from 'webui/utils/number';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
export function doOutputActionC2B(config, object, ids) {
    return {
        type: FixedBlockType.do_output_action,
        id: object.id,
        next: nextC2B(config, ids.slice(1)),
    };
}
export function doNotificationC2B(config, object, ids) {
    return {
        type: FixedBlockType.do_notification,
        id: object.id,
        next: nextC2B(config, ids.slice(1)),
    };
}
export function doCommandC2B(config, command, ids, type) {
    switch (type) {
        case FixedBlockType.do_pump:
            return {
                type,
                argument: ~~command.cmd,
                next: nextC2B(config, ids.slice(1)),
                id: command.objectid,
            };
        case FixedBlockType.do_relay:
            return {
                type,
                argument: ~~command.cmd,
                next: nextC2B(config, ids.slice(1)),
                id: command.objectid,
            };
        case FixedBlockType.do_buzzer:
            return {
                type,
                argument: ~~command.cmd,
                next: nextC2B(config, ids.slice(1)),
                id: command.objectid,
            };
        case FixedBlockType.do_tap:
            return {
                type,
                argument: ~~command.cmd,
                next: nextC2B(config, ids.slice(1)),
                id: command.objectid,
            };
        case FixedBlockType.set_guard:
            return {
                type,
                argument: ~~command.cmd,
                next: nextC2B(config, ids.slice(1)),
                id: command.objectid,
            };
        case FixedBlockType.do_temperature_in_circuit:
            return {
                type,
                target_temp: ~~command.cmd / 10 - 273,
                circuit: command.objectid,
                next: nextC2B(config, ids.slice(1)),
            };
    }
}
function breakC2B(config, object, ids) {
    return {
        type: FixedBlockType.do_break,
        next: nextC2B(config, ids.slice(1)),
    };
}
function continueC2B(config, object, ids) {
    return {
        type: FixedBlockType.do_continue,
        next: nextC2B(config, ids.slice(1)),
    };
}
function returnC2B(config, object, ids) {
    return {
        type: FixedBlockType.do_return,
        next: nextC2B(config, ids.slice(1)),
    };
}
function doScenarioC2B(config, object, ids) {
    return {
        type: FixedBlockType.do_scenario,
        scenario: object.id,
        next: nextC2B(config, ids.slice(1)),
    };
}
function doHeatModeForHeatCircuitC2B(config, object, ids) {
    let mode_id;
    let circuitOrAll;
    if (HeatingMode.get.byId(config, object.objectid)) {
        // There was a bug: mode_id and circuit_id were swapped
        // Here we detect this situation and swap it backwards
        mode_id = object.objectid;
        const cmd_num = ~~object.cmd;
        if (cmd_num == 1) {
            circuitOrAll = { for: 'all' };
        }
        else {
            circuitOrAll = { for: 'single', circuit: cmd_num };
        }
    }
    else {
        mode_id = ~~object.cmd;
        circuitOrAll = { for: 'single', circuit: object.objectid };
    }
    const mode_name = HeatingMode.get.byId(config, mode_id)?.name;
    return {
        type: FixedBlockType.do_heat_mode_for_heat_circuit,
        mode: mode_id,
        name: mode_name ?? 'неизвестный режим',
        next: nextC2B(config, ids.slice(1)),
        ...circuitOrAll,
    };
}
export function andOrNotC2B(config, object) {
    switch (object.type) {
        case LogicExpression.Type.AND:
            return {
                type: FixedBlockType.and,
                clauses: object.conditions.map((condition) => {
                    return resolverC2B(config, [condition]);
                }),
            };
        case LogicExpression.Type.OR:
            return {
                type: FixedBlockType.or,
                clauses: object.conditions.map((condition) => {
                    return resolverC2B(config, [condition]);
                }),
            };
        case LogicExpression.Type.NOT: {
            const block = object.conditions.map((condition) => {
                return resolverC2B(config, [condition]);
            });
            return {
                type: FixedBlockType.not,
                inp: block != null ? block[0] : null,
            };
        }
        default:
            throw new BlocklyError('andOrNotC2B', `неизвестный тип логического выражения ${object.type}`);
    }
}
function ifThenElseC2B(config, object, ids) {
    return {
        type: object.type === ConditionalOperator.Type.IF_THEN_ELSE
            ? FixedBlockType.if_then_else
            : FixedBlockType.if_then,
        comparator: resolverC2B(config, [object.comparator_id]),
        then: object.statementThen.length > 0
            ? resolverC2B(config, object.statementThen)
            : null,
        else: object.type === ConditionalOperator.Type.IF_THEN_ELSE
            ? object.statementElse.length > 0
                ? resolverC2B(config, object.statementElse)
                : null
            : null,
        next: nextC2B(config, ids.slice(1)),
    };
}
/**
 * @function
 * @param config - конфиг устройства
 * @param comparator - сравниватель с числом
 * @return number
 * возвращаю константy, деленную на нужный коэффициент, чтобы верно сравнивать
 * обратная функция getMultiplyConst
 */
function getDividedConst(config, comparator) {
    const sensor_reading = SensorReading.get.byId(config, comparator.left_object_id);
    if (sensor_reading != null) {
        const [object_type, object] = getZ3kEntityById(config, sensor_reading.obj_id);
        if (object != null) {
            switch (object_type) {
                case 'analog_inputs': {
                    return (comparator.right_const /
                        AnalogInput.getMultiplier(object));
                }
                case 'radiosensors': {
                    const radio_sensor = object;
                    const sensor_value_type = sensor_reading.argument;
                    if (radio_sensor.type === RadioSensor.Type.HUMIDITY) {
                        return comparator.right_const;
                    }
                    if (sensor_value_type === RadioSensorValue.BATTERY_LEVEL) {
                        return comparator.right_const;
                    }
                    if (sensor_value_type === RadioSensorValue.RSSI) {
                        return comparator.right_const;
                    }
                    return parseFloat(prettyFloat(comparator.right_const / 10 - 273, 1));
                }
                case 'wired_temperature_sensors':
                case 'analog_temperature_sensors':
                case 'boiler_adapters':
                    //ДециКельвины тут
                    return parseFloat(prettyFloat(comparator.right_const / 10 - 273, 1));
            }
        }
    }
    return comparator.right_const;
}
function numericComparatorC2B(config, object) {
    return {
        type: FixedBlockType.numeric_comparator,
        comparator_type: object.type,
        left: resolverC2B(config, [object.left_object_id]),
        right: getDividedConst(config, object),
    };
}
function sensorComparatorC2B(config, object) {
    return {
        type: FixedBlockType.sensor_comparator,
        comparator_type: object.type,
        left: resolverC2B(config, [object.left_object_id]),
        right: resolverC2B(config, [
            object.right_object_id == null
                ? 0
                : object.right_object_id,
        ]),
    };
}
function analogInputC2B(object) {
    return {
        type: FixedBlockType.analog_input,
        id: object.obj_id,
    };
}
function radioInputC2B(object) {
    return {
        type: FixedBlockType.radio_input,
        id: object.obj_id,
        value_type: object.argument,
    };
}
function sensorStateC2B(object, type) {
    return {
        type: type,
        id: object.obj_id != null ? object.obj_id : 0,
        argument: object.argument,
    };
}
function wiredTemperatureC2B(object) {
    return {
        type: FixedBlockType.temperature_input,
        id: object.obj_id,
    };
}
function pauseC2B(config, object, ids) {
    const [unit_time, value] = getUnitTime(object.time);
    return {
        type: FixedBlockType.pause,
        unit_time,
        value,
        next: nextC2B(config, ids.slice(1)),
    };
}
function timeComparatorC2B(config, object) {
    const min = object.time & 255;
    const hour = object.time >> 8;
    return {
        type: FixedBlockType.time_comparator,
        comparator_type: object.comparator_type,
        min: min,
        hour: hour,
    };
}
function weekdaysC2B(config, object) {
    return {
        type: FixedBlockType.weekdays_input,
        weekday_register: object.daysOfTheWeek,
    };
}
/**
 * @function
 * @param config - Z3KConfig прибора
 * @param ids - массив ID нужных для сценария
 * @return BlocklyBlock
 * Последовательно идём по ID сценария и парсим их
 */
export function resolverC2B(config, ids) {
    function objectPredicate(obj) {
        return obj.id === ids[0];
    }
    //logic
    const condition_operator = config.conditional_operators?.find(objectPredicate);
    if (condition_operator) {
        return ifThenElseC2B(config, condition_operator, ids);
    }
    const logic_expression = config.logic_expressions?.find(objectPredicate);
    if (logic_expression) {
        return andOrNotC2B(config, logic_expression);
    }
    const comparator = config.comparators?.find(objectPredicate);
    if (comparator) {
        if (comparator.right_object_id === 0 || comparator.right_object_id == null) {
            return numericComparatorC2B(config, comparator);
        }
        else {
            return sensorComparatorC2B(config, comparator);
        }
    }
    //do
    const notification = config.notifications?.find(objectPredicate);
    if (notification) {
        return doNotificationC2B(config, notification, ids);
    }
    const output_action = config.output_actions?.find(objectPredicate);
    if (output_action) {
        return doOutputActionC2B(config, output_action, ids);
    }
    const pause = config.pauses?.find(objectPredicate);
    if (pause) {
        return pauseC2B(config, pause, ids);
    }
    const scenario = config.scenarios?.find(objectPredicate);
    if (scenario) {
        return doScenarioC2B(config, scenario, ids);
    }
    const stop_scenario = config.stop_scenarios?.find(objectPredicate);
    if (stop_scenario) {
        switch (stop_scenario.type) {
            case StopScenario.Type.RETURN:
                return returnC2B(config, stop_scenario, ids);
            case StopScenario.Type.BREAK:
                return breakC2B(config, stop_scenario, ids);
            case StopScenario.Type.CONTINUE:
                return continueC2B(config, stop_scenario, ids);
        }
    }
    const command = config.commands?.find(objectPredicate);
    if (command) {
        if (command.objectid == null) {
            return null;
        }
        if (GuardZone.get.byId(config, command.objectid)) {
            return doCommandC2B(config, command, ids, FixedBlockType.set_guard);
        }
        if (RelayControl.get.byId(config, command.objectid)) {
            return doCommandC2B(config, command, ids, FixedBlockType.do_relay);
        }
        if (Pump.get.byId(config, command.objectid)) {
            return doCommandC2B(config, command, ids, FixedBlockType.do_pump);
        }
        if (Buzzer.get.byId(config, command.objectid)) {
            return doCommandC2B(config, command, ids, FixedBlockType.do_buzzer);
        }
        if (ThreewayTap.get.byId(config, command.objectid)) {
            return doCommandC2B(config, command, ids, FixedBlockType.do_tap);
        }
        if (HeatingCircuit.get.byId(config, command.objectid)) {
            if (HeatingMode.get.byId(config, ~~command.cmd)) {
                // For commands like #Y[circuit-id]=[mode-id]
                return doHeatModeForHeatCircuitC2B(config, command, ids);
            }
            else {
                return doCommandC2B(config, command, ids, FixedBlockType.do_temperature_in_circuit);
            }
        }
        //activate-heating-modes
        if (HeatingMode.get.byId(config, command.objectid)) {
            // For commands like #Y[mode-id]=1
            return doHeatModeForHeatCircuitC2B(config, command, ids);
        }
    }
    const sensor_reading = config.sensor_readings?.find(objectPredicate);
    if (sensor_reading) {
        const { type, obj_id } = sensor_reading;
        // sensor-states
        if (type === SensorReading.Type.STATE) {
            if (AnalogInput.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.analog_input_state);
            }
            if (Buzzer.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.buzzer_state);
            }
            if (GuardZone.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.guard_state);
            }
            if (RelayControl.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.relay_state);
            }
            if (ThreewayTap.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.tap_state);
            }
            if (Pump.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.pump_state);
            }
            if (WiredTemperatureSensor.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.wired_temperature_sensor_state);
            }
            if (BoilerAdapter.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.boiler_adapter);
            }
            if (RadioSensor.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.radio_sensor_state);
            }
            if (RadioSensor433.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.radio_sensor_433_state);
            }
            if (AnalogTemperatureSensor.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.analog_temperature_sensor_state);
            }
            if (HeatingCircuit.get.byId(config, obj_id)) {
                return sensorStateC2B(sensor_reading, FixedBlockType.state_heat_mode_for_heat_circuit);
            }
        }
        if (type === SensorReading.Type.VALUE) {
            if (AnalogInput.get.byId(config, obj_id)) {
                return analogInputC2B(sensor_reading);
            }
            if (RadioSensor.get.byId(config, obj_id)) {
                return radioInputC2B(sensor_reading);
            }
            if (WiredTemperatureSensor.get.byId(config, obj_id)) {
                return wiredTemperatureC2B(sensor_reading);
            }
            if (AnalogTemperatureSensor.get.byId(config, obj_id)) {
                return wiredTemperatureC2B(sensor_reading);
            }
            if (BoilerAdapter.get.byId(config, obj_id)) {
                return wiredTemperatureC2B(sensor_reading);
            }
        }
    }
    //time
    const time_comparator = config.time_comparators?.find(objectPredicate);
    if (time_comparator) {
        if (time_comparator.type === TimeComparator.Type.TIME)
            return timeComparatorC2B(config, time_comparator);
        if (time_comparator.type === TimeComparator.Type.WEEKDAYS)
            return weekdaysC2B(config, time_comparator);
    }
    return null;
}
export function nextC2B(config, ids) {
    if (ids.length === 0)
        return null;
    return resolverC2B(config, ids);
}
